import { LiteralUnion } from 'models';
import type { PlanType } from 'models/bfmc';
import { LOCALSTORAGE, API_URL } from 'config';

import { makeMockAPICall, makeAPICall } from 'models/api';

import AbstractLayer from 'controllers/AbstractLayer';
import OSMLayer from 'controllers/OSMLayer';
import TileWMSLayer from 'controllers/WMSTileLayer';
import MapServerTileLayer from 'controllers/MapServerTileLayer';
import CommentLayer from 'controllers/CommentsLayer';
import RedlineLayer from 'controllers/RedlineLayer';
import WMSEditableLayer from 'controllers/WMSEditableLayer';
import ImageArcGISRestLayer from 'controllers/ImageArcGISRestLayer';
import VectorTileLayer from 'controllers/VectorTileLayer';
import XYZLayer from 'controllers/XYZLayer';
import CompositeLayer from 'controllers/CompositeLayer';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';

/**
 * Allowed Layer Types, each corresponds to an Implementation type of the AbstractLayer
 */
export type LayerType =
    | 'TileWMS'
    | 'OSM'
    | 'TileMapServer'
    | 'CommentLayer'
    | 'RedlineLayer'
    | 'WMSEditableLayer'
    | 'WFSFilteredLayer'
    | 'ImageArcGISRestLayer'
    | 'VectorTile'
    | 'XYZ'
    | 'Composite';

/**
 * A list of Sections that the layers can be assigned to.
 * These will also result in the layers living in their own side drawer
 */
export enum SectionEnum {
    'Assets',
    'Risk Results',
    'Treatments and Edits',
    'Boundaries and Basemaps',
}

/**
 * A list of all groups that the layers can be assigned to.
 * These would result in all layers within the same Section
 * and Group to be grouped together under the title of this
 * group name.
 */
export enum GroupsEnum {
    'Street Map',
    'Satellite Map',
    'Satelite',
    'Terrain',
    'Basemaps',
    'Edits',
    'Boundaries',
    'Residential and SFPP',
    'Agriculture',
    'Commercial and Industrial',
    'Electricity',
    'Plantations',
    'Infrastructure',
    'Fire Behaviour',
    'Assets',
    'Residential',
    'Residential By Grid',
    'Residential Embers',
    'Special Fire Protection Purpose (SFPP)',
    'Economic',
    'Environmental',
    'Aboriginal Known',
    'Aboriginal Predicted',
    'Historic Heritage',
    'Times Burnt',
    'Preliminary Risk Layers',
    'EXAMPLE RISK LAYERS FROM DEW',
    'WMS',
    'Test',
    'Bushfire Management Area Plans',
    'Property Land Use Based',
    'Other',
    'Adhoc',
    'Reference',
}

/**
 * A list of all LayerIds. While it may double up on a bit of work
 * to have to add the ID here before adding the config, the result
 * is that this Enum can easily be used to build the resulting type
 * along with a list that can be iterated over, aiding in developer
 * experience
 */
export enum LayerEnum {
    'OSM',
    'SA_StreetMap',
    'SA_Topographic',
    'SA_Imagery',
    'SA_ImageryLabels',
    'Comments',
    'Redline',
    'FocusArea',
    'FuelManagementRegister',
    'FaftLine',
    'Bldg_Residential',
    'Bldg_Schools',
    'Bldg_Hospitals',
    'Bldg_CareFacilities',
    'Agriculture',
    'Ag_Livestock',
    'Ag_Horticulture',
    'Ag_Poultry',
    'Bldg_Commercial',
    'Bldg_Industrial',
    'Elect_PowerGeneration',
    'Elect_SAPN_OverheadLV',
    'Elect_SAPN_OverheadHV',
    'Plantations_Forestry',
    'Infra_Telecom',
    'MockRoleRestricted_TestLayer',
    'Enviro_NPWSA_Reserves',
    'Admin_BMC',
    'Admin_LGA',
    'Fire_History',
    'Adhoc_Pt',
    'Adhoc_Line',
    'Adhoc_Poly',
    'FAFT_FireAccessTracks',
    'FAFT_Roads',
    'MVW_Bfrp_Plan_Active_BFRMP',
    'MVW_FMR_Approved',
    'MVW_FMR_Draft',
    'MVW_FocusArea_Approved',
    'MVW_FocusArea_Draft',
    'Risk_Economic_Current',
    'Risk_Economic_Future',
    'Risk_Economic_FuturePostTreatment',
    'Risk_Economic_Increase',
    'Risk_Economic_Reduction',
    'Risk_Environmental_Current',
    'Risk_Environmental_Future',
    'Risk_Environmental_FuturePostTreatment',
    'Risk_Environmental_Increase',
    'Risk_Environmental_Reduction',
    'Risk_AboriginalKnown_Current',
    'Risk_AboriginalKnown_Future',
    'Risk_AboriginalKnown_FuturePostTreatment',
    'Risk_AboriginalKnown_Increase',
    'Risk_AboriginalKnown_Reduction',
    'Risk_AboriginalPredicted_Current',
    'Risk_AboriginalPredicted_Future',
    'Risk_AboriginalPredicted_FuturePostTreatment',
    'Risk_AboriginalPredicted_Increase',
    'Risk_AboriginalPredicted_Reduction',
    'Risk_HistoricHeritage_Current',
    'Risk_HistoricHeritage_Future',
    'Risk_HistoricHeritage_FuturePostTreatment',
    'Risk_HistoricHeritage_Increase',
    'Risk_HistoricHeritage_Reduction',
    'Risk_ResidentialEmbers_Current',
    'Risk_ResidentialEmbers_Future',
    'Risk_ResidentialEmbers_FuturePostTreatment',
    'Risk_ResidentialEmbers_Increase',
    'Risk_ResidentialEmbers_Reduction',
    'Risk_Residential_Current',
    'Risk_Residential_Future',
    'Risk_Residential_FuturePostTreatment',
    'Risk_Residential_Increase',
    'Risk_Residential_Reduction',
    'Risk_ResidentialByGrid_Current',
    'Risk_ResidentialByGrid_Future',
    'Risk_ResidentialByGrid_FuturePostTreatment',
    'Risk_ResidentialByGrid_Increase',
    'Risk_ResidentialByGrid_Reduction',
    'Risk_SFPP_Current',
    'Risk_SFPP_Future',
    'Risk_SFPP_FuturePostTreatment',
    'Risk_SFPP_Increase',
    'Risk_SFPP_Reduction',
    'Risk_TimesBurnt_Current',
    'Risk_TimesBurnt_Future',
    'Risk_TimesBurnt_FuturePostTreatment',
    'Risk_TimesBurnt_Increase',
    'Risk_TimesBurnt_Reduction',
    'FireBehaviour_Ignitions',
    'FireBehaviour_FuelLoad',
    'FireBehaviour_FuelType',
    'Preliminary_Ignitions',
    'Preliminary_FuelLoad',
    'Preliminary_TimesBurnt',
    'Preliminary_ResidentialRiskByDensity',
    'Preliminary_ResidentialEmbersRisk',
    'ClientRisk_AMLR_FL_HouseLossIgnition',
    'ClientRisk_AMLR_FL_PostBurnHouseLoss',
    'ClientRisk_KI_RiskSimulation',
    'ClientRisk_KI_IgnitionImpact',
    'ClientRisk_MM_TimesBurnt',
    'ClientRisk_MM_HouseLoss',
    'BMC_Mask',
}

export interface LayerFilter {
    fieldName: string;
    operator: string;
    expression: string;
}

// layer may be editable in other ways.
export enum EditableLayers {
    FuelManagementRegister = 'FuelManagementRegister',
    FocusArea = 'FocusArea',
}

export type EditFeature = (
    layerName: EditableLayers,
) => ((feature: Feature<Geometry>) => void) | undefined | null;

export declare namespace Layer {
    export type Group = keyof typeof GroupsEnum;

    export type Id = keyof typeof LayerEnum;

    export type Section = keyof typeof SectionEnum;

    export type Status =
        | 'normal'
        | 'prending-update'
        | 'processing'
        | 'error'
        | 'not yet available'
        | 'unavailable';

    export namespace API {
        export interface LayerSummary {
            product_code: string;
            status: string;
            last_updated: string;
            time_steps: string[];
        }

        export interface Root {
            data: LayerSummary[];
            count: number;
        }
    }
}

export interface GroupConfig {
    /**
     * The Group Id
     */
    id: Layer.Group;
    /**
     * If this Group should be rendered as an Accordion
     */
    collapsable: boolean;
    /**
     * If {@link collapsable} is true, then this is a flag for
     * if the Accordion should start closed. False means it will
     * start open
     */
    defaultCollapsed: boolean;
}

export type LegendType = 'Line' | 'Raster' | 'Polygon' | 'Point';

export interface LegendRenderingOptions {
    globalType?: LegendType;
}

export interface IdentifyRenderOptions {
    naming?: {
        [key: string]: string;
    };
    exclude?: string[];
    dateOnlyFields?: string[];
}

export declare namespace API {
    export interface LayerRestriction {
        layer_id: string;
        layer_name: string;
        allowed_roles: string[];
    }

    export interface LayerMetadata {
        bfmc_id: number;
        layer_id: string;
        layer_name: string;
        layer_group: string;
        layer_provider: string;
        layer_description: string;
        layer_updated_on: string | null; // '2025-06-27T02:20:29.292932+00:00'
        updated_on: string | null; //'2025-06-04T23:34:57.192Z';
        updated_by: string;
        can_update_metadata: boolean;
        is_state_level: boolean;
    }
}

export interface LayerRestriction {
    layerId: string;
    layerName: string;
    allowedRoles: string[];
}

export interface LayerMetadata {
    bfmcId: number;
    layerId: string;
    layerName: string;
    layerGroup: string;
    layerProvider: string;
    layerDescription: string;
    layerUpdatedOn: Date | null; // '2025-06-27T02:20:29.292932+00:00'
    updatedOn: Date | null; //'2025-06-04T23:34:57.192Z';
    updatedBy: string;
    canUpdateMetadata: boolean;
    isStateLevel: boolean;
}

export const mapAPIToLayerRestrictions = ({
    layer_name,
    layer_id,
    allowed_roles,
    ...rest
}: API.LayerRestriction): LayerRestriction => ({
    ...rest,
    layerId: layer_id,
    layerName: layer_name,
    allowedRoles: allowed_roles,
});

const FIRE_TRAIL_ROUTE = `${API_URL}/layers`;

export const getLayerRestrictions = makeAPICall<
    LayerRestriction[],
    void,
    API.LayerRestriction[]
>(
    () => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/restricted`,
        };
    },
    (raw) => raw.map((l) => mapAPIToLayerRestrictions(l)),
);

export const filterLayersFromRestrictions = <T extends { id: string }>(
    layerConfigs?: T[] | null,
    layerRestrictions?: LayerRestriction[] | null,
    userRoles?: string[] | null,
) => {
    // If no user roles, they shouldn't see anything. This may occur briefly while loading.
    if (!userRoles || userRoles.length === 0) return [];

    return (
        layerConfigs?.filter((layer) => {
            const restrictions = layerRestrictions?.find(
                (r) => r.layerId == layer.id,
            );
            // If there are no restrictions found for layer, the layer is allowed
            if (restrictions == null) return true;

            // Allow if there is any overlap between userRoles and allowedRoles
            return restrictions.allowedRoles.some((role) =>
                userRoles.includes(role),
            );
        }) ?? []
    );
};

export const mapAPIToLayerMetadata = ({
    bfmc_id,
    layer_id,
    layer_name,
    layer_group,
    layer_provider,
    layer_description,
    layer_updated_on,
    updated_on,
    updated_by,
    can_update_metadata,
    is_state_level,
    ...rest
}: API.LayerMetadata): LayerMetadata => ({
    ...rest,
    bfmcId: bfmc_id,
    layerId: layer_id,
    layerName: layer_name,
    layerGroup: layer_group,
    layerProvider: layer_provider,
    layerDescription: layer_description,
    layerUpdatedOn: layer_updated_on ? new Date(layer_updated_on) : null,
    updatedOn: updated_on ? new Date(updated_on) : null,
    updatedBy: updated_by,
    canUpdateMetadata: can_update_metadata,
    isStateLevel: is_state_level,
});

export const getLayerMetadata = makeAPICall<
    LayerMetadata[],
    { bfmc_id?: number | null },
    API.LayerMetadata[]
>(
    ({ bfmc_id }) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/metadata/${bfmc_id}`,
        };
    },
    (raw) => raw.map((l) => mapAPIToLayerMetadata(l)),
);

export const updateLayerMetadata = makeAPICall<
    LayerMetadata,
    { layer_id: string; bfmc_id?: number | null; layer_updated_on: string },
    API.LayerMetadata
>(
    ({ layer_id, bfmc_id, layer_updated_on }) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/metadata/${layer_id}`,
            requestData: {
                method: 'PUT',
                body: JSON.stringify({
                    layer_updated_on,
                    bfmc_id,
                }),
            },
        };
    },
    (raw) => mapAPIToLayerMetadata(raw),
);

export interface LayerConfig {
    /**
     * The Layer's Id; It should be listed in Layers Enum, if not
     * add it to the Enum first to prevent an error
     */
    id: Layer.Id;
    /**
     * A User Friendly Name for the layer.
     */
    name: string;
    /**
     * The Type of this layer, will inform which AbstractLayer
     * implementation will be used.
     */
    type: LayerType;
    /**
     * Restricts this layer to a single plan type. When set, the layer is only
     * listed/visible while guide.selectedPlanType matches (gated in LayerList).
     * Omit for layers that apply to every plan type (the default).
     */
    planType?: PlanType;
    /**
     * The full string of the Geoserver service name.
     * Should be null if not connecting to the configured Geoserver.
     */
    serviceName?: string | null;

    /**
     * Layer feature namespace to support editing functionalities.
     */
    featureNS?: string | undefined;

    /**
     * Coordinate reference system, to support editing functionalities.
     */
    srsName?: string | undefined;
    /**
     * The URL of the service to connect to. Generally
     * only used when using non-standard Geoserver services.
     * Ie: An ESRI MapService externally hosted.
     */
    url?: string;
    /**
     * Mapbox GL Style spec URL for VectorTile layers. Used by
     * ol-mapbox-style to apply the published cartography.
     */
    styleUrl?: string;
    /**
     * Named GeoServer style to request via the WMS STYLES parameter.
     * When set, overrides the layer's default style without creating a
     * separate GeoServer layer config.
     */
    wmsStyles?: string;
    /**
     * Child layer specs for Composite basemaps. Rendered bottom-up
     * in array order — first entry sits underneath subsequent ones.
     */
    compositeChildren?: { type: LayerType; url: string }[];
    /**
     * Flag to indicate to pass congintio token for the request
     */
    tokenRequired?: boolean | undefined;
    /**
     * A number between 0 and 1 representing the default
     * opacity of the layer
     */
    opacity?: number;
    /**
     * Used by ArcGIS MapService layer id to show.. currently support only one
     */
    mapserviceLayerId?: number | undefined;
    /**
     * Layer min scale for layer visibility
     */
    minScale?: number | undefined;
    /**
     * Layer max scale for layer visibility
     */
    maxScale?: number | undefined;
    /**
     * Used by ArcGIS MapService layers to show
     */
    layerDefs?: string | undefined;
    /**
     * The path to the thumbnail image to use with this layer
     */
    thumbnail?: string;
    /**
     * A short summary of the layer. Used in the popover that
     * is displayed when the user clicks on the layer.
     * It's intended to provide more information about the layer.
     */
    summary?: string;
    /**
     * A Flag for if the layer is time enabled
     */
    isTimeEnabled?: boolean;
    /**
     * A Flag for if a layer should be treated as a 'basemap'.
     * All this changes is that only 1 basemap layer type can be
     * selected at one time.
     */
    isBaseMap?: boolean;
    /**
     * A Flag for if the Layer should be treated as a 'boundary' layer.
     * This was originally used in AFDRS and doesn't have any impact here.
     */
    isBoundary?: boolean;
    /**
     * A Flag for if the Layer should be treated as a 'utility' layer.
     * This was originally used in AFDRS and doesn't have any impact here.
     */
    isUtility?: boolean;
    /**
     * A Flag for if the Layer should be treated as a 'hidden' layer.
     * This was originally used in AFDRS and doesn't have any impact here.
     * If required again, it's originally intended to make the layer not show
     * up on lists, yet still be created and interacted with programatically.
     */
    isHidden?: boolean;
    /**
     * The section this layer is assigned to
     */
    section?: Layer.Section;
    /**
     * A Flag for if the Layer has a last updated time, and if often updated.
     * If True it will attempt to get a time (for this project there generally
     * won't be) and display it on the Layer List.
     */
    isNotUpdateable?: boolean;
    /**
     * A Flag for if true, will inform the Legend to not bother getting and
     * displaying this layer's legend, as it likely doesn't exist or isn't
     * useful.
     */
    noLegend?: boolean;
    /** When true, each GeoServer rule title is used as the legend entry label instead of the layer name. */
    classifiedLegend?: boolean;
    /** Legend entries whose label matches any of these strings (case-insensitive) are excluded from the legend display. */
    legendExcludeLabels?: string[];
    /**
     * A Flag for if true, skips this layer during the map identify (click)
     * flow so it never contributes to the feature popup. Use for raster
     * coverage layers where GetFeatureInfo returns a raw GRAY_INDEX value
     * with no useful context for end users.
     */
    noPopup?: boolean;
    /**
     * A Flag for if true, will use the Thumbnail image for the Legend Entry.
     * Only Useful if the Legend configuration is unusually annoying to support
     * and that the Thumbnail is representative.
     */
    thumbnailLegend?: boolean;
    /**
     * The Group the layer is assigned to
     */
    group: Layer.Group;
    /**
     * The Layer Status; Not Used for this project and leftover from AFDRS.
     * Is intended to be updated with the Layers Status when an API Endpoint
     * is queried.
     */
    status: Layer.Status;
    /**
     * The Units the Layers Value is in. Used in the Legend along with
     * anywhere were the Layers pixel value may be used.
     */
    units?: string;
    /**
     * The number of decimal places to round this Layers pixel value.
     */
    precision?: number;
    /**
     * If there is any error with this Layer, this describes it.
     */
    error?: string;
    /**
     * The Field to use as a label for MapboxVectorTiles layers.
     * Unused in this project so far.
     */
    mvtLabelField?: string;
    /**
     * The Id of an optional 'Child Layer', Ie: A layer
     * that should only be displayed while this one is visible.
     * Is also leftover from AFDRS
     */
    childLayer?: Layer.Id;
    /**
     * Unused: Is also leftover from AFDRS
     */
    lastUpdated: number | null;
    /**
     * Unused: Is also leftover from AFDRS
     */
    timeSteps: Date[] | null;
    /*
     * Field to determine CQL Filter required from filtering
     * the data based on selected BMC
     */
    filters?: LayerFilter[];

    /**
     * Field to store legend details for external layers
     */
    legend?: any | null;

    /**
     * Field to identify whether the layer is allowed for copying
     * feature for importing to fire trail dataset
     */
    supportCopyFeature?: boolean;

    /**
     * Unique id field name used by data import for fetching
     * geometry from db
     */
    copyByAttributeField?: string;
    /**
     * Field to store rendering options for the legend
     */
    legendRenderingOptions?: LegendRenderingOptions;

    identifyRenderOptions?: IdentifyRenderOptions;

    /**
     * Gets a function back from an object of edit functions
     * if it return a function invoke it with the correct editable layer type.
     * if does not have correct permissions don't invoke.
     */
    editFeature?: (edit: EditFeature) => ReturnType<EditFeature>;
}

/**
 * A List of Layer Sections
 */
export const sections = Object.keys(SectionEnum).filter((x) =>
    Number.isNaN(+x),
) as Layer.Section[];

/**
 * A List of Layer Groups
 */
export const groups = Object.keys(GroupsEnum).filter((x) =>
    Number.isNaN(+x),
) as Layer.Group[];

/**
 * A List of all Layer Ids
 */
export const layerIds = Object.keys(LayerEnum).filter((x) =>
    Number.isNaN(+x),
) as Layer.Id[];

/**
 * Client-facing relabelling of a small set of layer groups. The internal
 * group keys (used as identifiers in {@link GroupsEnum}, layer configs and
 * group settings) stay stable; only the title shown in the layer list is
 * remapped here. Groups not listed display their key verbatim. Titles are
 * uppercased by the layer-list styling, so these are written in title case.
 */
const groupDisplayNames: Partial<Record<Layer.Group, string>> = {
    'EXAMPLE RISK LAYERS FROM DEW': 'Example Risk Layers',
    'Fire Behaviour': 'Mock Fire Behaviour',
    Residential: 'Mock Residential',
    Economic: 'Mock Economic',
    Environmental: 'Mock Environmental',
    'Times Burnt': 'Mock Times Burnt',
};

/**
 * @param group A layer group key
 * @returns The client-facing title for the group, falling back to the key.
 */
export const groupDisplayName = (group: string): string =>
    groupDisplayNames[group as Layer.Group] ?? group;

/**
 * @param id A string to test
 * @returns True if the input is a LayerId. The Type is also enforced.
 */
export const isValidLayer = (id: string): id is Layer.Id =>
    layerIds.indexOf(id as Layer.Id) > -1;

/**
 * The Default settings for each Layer Group
 */
const defaultGroupSettings: Omit<GroupConfig, 'id'> = {
    collapsable: false,
    defaultCollapsed: false,
};

/**
 * Group Configurations can be placed here. Any group not added
 * will be added with some defaults.
 */
const unprocessedGroupConfig: Partial<
    Record<Layer.Group, Omit<GroupConfig, 'id'>>
> = {
    'Residential and SFPP': {
        collapsable: true,
        defaultCollapsed: true,
    },
    Agriculture: {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Commercial and Industrial': {
        collapsable: true,
        defaultCollapsed: true,
    },
    Electricity: {
        collapsable: true,
        defaultCollapsed: true,
    },
    Plantations: {
        collapsable: true,
        defaultCollapsed: true,
    },
    Infrastructure: {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Fire Behaviour': {
        collapsable: true,
        defaultCollapsed: true,
    },
    Boundaries: {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Bushfire Management Area Plans': {
        collapsable: true,
        defaultCollapsed: true,
    },
    Adhoc: {
        collapsable: true,
        defaultCollapsed: true,
    },
    Residential: {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Special Fire Protection Purpose (SFPP)': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Residential By Grid': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Residential Embers': {
        collapsable: true,
        defaultCollapsed: true,
    },
    Economic: {
        collapsable: true,
        defaultCollapsed: true,
    },
    Environmental: {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Aboriginal Known': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Aboriginal Predicted': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Historic Heritage': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Times Burnt': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Preliminary Risk Layers': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'EXAMPLE RISK LAYERS FROM DEW': {
        collapsable: true,
        defaultCollapsed: true,
    },
    'Property Land Use Based': {
        collapsable: true,
        defaultCollapsed: true,
    },
    Other: {
        collapsable: true,
        defaultCollapsed: true,
    },
    Reference: {
        collapsable: true,
        defaultCollapsed: true,
    },
};

/**
 * The Layer Group Configurations.
 */
export const groupConfig: Record<Layer.Group, GroupConfig> = groups.reduce<
    Record<Layer.Group, GroupConfig>
>(
    (acc, val) => {
        acc[val] = {
            id: val,
            ...defaultGroupSettings,
            ...unprocessedGroupConfig[val],
        };
        return acc;
    },
    {} as Record<Layer.Group, GroupConfig>,
);

// Risk Results matrix: 10 asset categories × 5 metric scenarios = 50 placeholder
// layers for SA UAT. All point at risk-planning:RiskMock so a single statewide
// mock raster (loaded later) backs every cell of the matrix. When real per-BMC
// rasters land, change serviceName here per group/asset; the data_src CQL
// filter is already wired for BMC tenancy.
const RISK_METRICS: { suffix: string; label: string; isHidden?: boolean }[] = [
    { suffix: 'Current', label: 'Current' },
    { suffix: 'Future', label: 'Future (without treatments)' },
    { suffix: 'FuturePostTreatment', label: 'Future (with treatments)' },
    {
        suffix: 'Increase',
        label: 'Climate Increase (untreated)',
        isHidden: true,
    },
    { suffix: 'Reduction', label: 'Treatment Reduction', isHidden: true },
];

const RISK_ASSETS: {
    slug: string;
    label: string;
    group: Layer.Group;
}[] = [
    { slug: 'Economic', label: 'Economic Risk', group: 'Economic' },
    {
        slug: 'Environmental',
        label: 'Environmental Risk',
        group: 'Environmental',
    },
    {
        slug: 'AboriginalKnown',
        label: 'Aboriginal Known Risk',
        group: 'Aboriginal Known',
    },
    {
        slug: 'AboriginalPredicted',
        label: 'Aboriginal Predicted Risk',
        group: 'Aboriginal Predicted',
    },
    {
        slug: 'HistoricHeritage',
        label: 'Historic Heritage Risk',
        group: 'Historic Heritage',
    },
    {
        slug: 'ResidentialEmbers',
        label: 'Residential Embers Risk',
        group: 'Residential Embers',
    },
    { slug: 'Residential', label: 'Residential Risk', group: 'Residential' },
    {
        slug: 'ResidentialByGrid',
        label: 'Residential Risk (by Grid)',
        group: 'Residential By Grid',
    },
    {
        slug: 'SFPP',
        label: 'SFPP Risk',
        group: 'Special Fire Protection Purpose (SFPP)',
    },
    { slug: 'TimesBurnt', label: 'Times Burnt', group: 'Times Burnt' },
];

const riskMatrixLayers: Omit<
    LayerConfig,
    'lastUpdated' | 'timeSteps' | 'status'
>[] = RISK_ASSETS.flatMap((asset) =>
    RISK_METRICS.map(({ suffix, label, isHidden }) => ({
        id: `Risk_${asset.slug}_${suffix}` as Layer.Id,
        name: `${asset.label} — ${label}`,
        type: 'TileWMS' as const,
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/temperature.png',
        summary: `${asset.label} (${label}) — placeholder layer for SA UAT.`,
        section: 'Risk Results' as const,
        group: asset.group,
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        ...(isHidden && { isHidden }),
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    })),
);

/**
 * Use this Layer Configuration to configure all layers
 * that should be instanciated.
 */
const unprocessedlayerConfig: Omit<
    LayerConfig,
    'lastUpdated' | 'timeSteps' | 'status'
>[] = [
    {
        id: 'OSM',
        name: 'Open Street Maps',
        type: 'OSM',
        serviceName: null,
        thumbnail: '/thumbnails/osm-logo.png',
        opacity: 1,
        summary: 'Open Source Street Map',
        isBaseMap: true,
        isBoundary: false,
        isUtility: false,
        section: 'Boundaries and Basemaps',
        group: 'Basemaps',
        isNotUpdateable: true,
    },
    {
        id: 'SA_StreetMap',
        name: 'SA Street Map',
        type: 'TileMapServer',
        serviceName: null,
        url: 'https://basemap.geohub.sa.gov.au/arcgis/rest/services/BaseMaps/StreetMapCased_wmas/MapServer',
        thumbnail: '/thumbnails/lpi_basemap.png',
        opacity: 1,
        summary:
            'Location SA street base map with cased road symbology and parcel cadastre at larger scales.',
        isBaseMap: true,
        isBoundary: false,
        isUtility: false,
        section: 'Boundaries and Basemaps',
        group: 'Basemaps',
        isNotUpdateable: true,
    },
    {
        id: 'SA_Topographic',
        name: 'SA Topographic',
        type: 'TileMapServer',
        serviceName: null,
        url: 'https://basemap.geohub.sa.gov.au/arcgis/rest/services/BaseMaps/Topographic_wmas/MapServer',
        thumbnail: '/thumbnails/lpi_basemap.png',
        opacity: 1,
        summary:
            'Location SA topographic base map with terrain relief, contours and land-use shading.',
        isBaseMap: true,
        isBoundary: false,
        isUtility: false,
        section: 'Boundaries and Basemaps',
        group: 'Basemaps',
        isNotUpdateable: true,
    },
    {
        id: 'SA_Imagery',
        name: 'SA Imagery',
        type: 'XYZ',
        serviceName: null,
        url: 'https://imagemap.geohub.sa.gov.au/mapproxy/wmts/PublicMosaic/webmercator_22/{z}/{x}/{y}.png',
        thumbnail: '/thumbnails/lpi_imagery.png',
        opacity: 1,
        summary:
            'Location SA Public Mosaic imagery — best available state aerial imagery.',
        isBaseMap: true,
        isBoundary: false,
        isUtility: false,
        section: 'Boundaries and Basemaps',
        group: 'Basemaps',
        isNotUpdateable: true,
    },
    {
        id: 'SA_ImageryLabels',
        name: 'SA Imagery with Labels',
        type: 'Composite',
        serviceName: null,
        thumbnail: '/thumbnails/lpi_imagery.png',
        opacity: 1,
        summary:
            'Location SA Public Mosaic imagery with street, LGA and suburb labels overlaid.',
        isBaseMap: true,
        isBoundary: false,
        isUtility: false,
        section: 'Boundaries and Basemaps',
        group: 'Basemaps',
        isNotUpdateable: true,
        compositeChildren: [
            {
                type: 'XYZ',
                url: 'https://imagemap.geohub.sa.gov.au/mapproxy/wmts/PublicMosaic/webmercator_22/{z}/{x}/{y}.png',
            },
            {
                // ArcGIS MapServer's pre-cached tile endpoint, matching what
                // Esri MapViewer uses — labels render at the scales the server
                // designer intended. Swapping to /export would re-render on
                // demand at arbitrary BBOXes, causing label-size drift.
                type: 'XYZ',
                url: 'https://lsa1.geohub.sa.gov.au/server/rest/services/BaseMaps/StreetLabels_wmas/MapServer/tile/{z}/{y}/{x}',
            },
        ],
    },
    {
        id: 'Comments',
        name: 'Comments',
        type: 'CommentLayer',
        serviceName: null,
        thumbnail: '/thumbnails/comment.svg',
        opacity: 1,
        summary:
            'All comments displayed as points on the map along with their replies.',
        isBaseMap: false,
        isBoundary: false,
        isUtility: true,
        section: 'Treatments and Edits',
        group: 'Edits',
        isNotUpdateable: true,
        thumbnailLegend: true,
        legend: { layerName: 'Comments', title: 'Comments', rules: [] },
    },
    {
        id: 'Redline',
        name: 'Drawings',
        type: 'RedlineLayer',
        serviceName: 'risk-planning:Redlining',
        thumbnail: '/thumbnails/edit.svg',
        opacity: 1,
        summary: 'A Marker layer displaying various redlines used in workshops',
        isBaseMap: false,
        isBoundary: false,
        isUtility: true,
        section: 'Treatments and Edits',
        group: 'Edits',
        isNotUpdateable: true,
        thumbnailLegend: true,
        noPopup: true,
    },
    {
        id: 'FocusArea',
        name: 'Pilot Areas',
        type: 'WMSEditableLayer',
        // BMAP-only: Pilot Areas is a BFRMP treatment layer, hidden in FAFT mode.
        planType: 'bfrmp',
        serviceName: 'risk-planning:FocusArea',
        thumbnail: '/thumbnails/focus_area.svg',
        opacity: 1,
        summary: 'A Feature Layer displaying the BMC Pilot Areas',
        isBaseMap: false,
        isBoundary: false,
        isUtility: true,
        section: 'Treatments and Edits',
        group: 'Edits',
        isNotUpdateable: true,
        filters: [
            {
                // GeoServer column is "BmcId" (renamed from "BfmcId" in the
                // BFMC→BMC migration); the old name made WMS GetMap fail with
                // "Property 'BfmcId' could not be found" → blank overlay.
                fieldName: 'BmcId',
                operator: '=',
                expression: 'guide.selectedBFMC?.id',
            },
            {
                fieldName: 'PlanId',
                operator: '=',
                expression: 'guide.selectedPlan?.id',
            },
            {
                fieldName: 'Status',
                operator: '<>',
                expression: '"Withdrawn"',
            },
        ],
        editFeature: (edit) => edit(EditableLayers.FocusArea),
        identifyRenderOptions: {
            exclude: ['Code'],
            naming: {
                RiskProfil: 'Risk Drivers',
                StatusComm: 'Description',
            },
        },
    },
    {
        // Editable FAFT fire-trail lines. Mirrors the FocusArea entry above so
        // GISViewer.findLayer('FaftLine') resolves and the "Draft FAFT Plan"
        // tool (FireTrailToolControls) renders. Geometry edits draw via
        // WMSEditTool but persist through the /fire-trails API (FireTrailActions),
        // then this layer is .refresh()ed — there is no WFS-T transaction.
        // No editFeature: fire-trail editing is driven by the accordion's own
        // dialog, not the map-click FeaturePopup path used by FocusArea/FMR.
        id: 'FaftLine',
        name: 'FAFT Fire Trails',
        type: 'WMSEditableLayer',
        // FAFT-only: hidden from the layer list / map in BMAP (bfrmp) mode.
        planType: 'faft',
        serviceName: 'risk-planning:FaftLine',
        thumbnail: '/thumbnails/fire_trails.png',
        opacity: 1,
        summary: 'Draft FAFT fire-trail lines for the selected BMC plan.',
        isBaseMap: false,
        isBoundary: false,
        isUtility: true,
        section: 'Treatments and Edits',
        group: 'Edits',
        isNotUpdateable: true,
        legendRenderingOptions: {
            globalType: 'Line',
        },
        filters: [
            // Scope the WMS render to the active BMC + FAFT plan. BmcId/PlanId
            // are the published GeoServer attribute names (raw geoserver."FaftLine"
            // columns, both NOT NULL FKs). FAFT has no "Withdrawn" status, so —
            // unlike FocusArea — there is no Status exclusion filter.
            {
                fieldName: 'BmcId',
                operator: '=',
                expression: 'guide.selectedBFMC?.id',
            },
            {
                fieldName: 'PlanId',
                operator: '=',
                expression: 'guide.selectedPlan?.id',
            },
        ],
        identifyRenderOptions: {
            exclude: [
                'Id',
                'GlobalId',
                'FAFTId',
                'FAFTSegmentId',
                'TopoId',
                'SourceFireTrailId',
                'SourceSegmentId',
                'PlanId',
                'BmcId',
            ],
            naming: {
                FireTrailName: 'Fire Trail Name',
                RoadNameBase: 'Road Name',
                VehicleCarryingCapacity: 'Vehicle Carrying Capacity',
                WorksType: 'Works Type',
                LeadAgency: 'Lead Agency',
            },
            dateOnlyFields: ['Designated'],
        },
    },
    {
        id: 'FAFT_FireAccessTracks',
        name: 'Fire Access Tracks',
        type: 'TileWMS',
        serviceName: 'risk-planning:FAFT_FireAccessTracks',
        thumbnail: '/thumbnails/fire_trails.png',
        summary: 'Fire Access Tracks sourced from SA Roads dataset.',
        section: 'Boundaries and Basemaps',
        group: 'Reference',
        isNotUpdateable: true,
        supportCopyFeature: true,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        legendRenderingOptions: {
            globalType: 'Line',
        },
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'FAFT_Roads',
        name: 'Roads',
        type: 'TileWMS',
        serviceName: 'risk-planning:FAFT_Roads',
        thumbnail: '/thumbnails/road_segments.png',
        summary: 'SA Roads (non-fire-access segments).',
        section: 'Boundaries and Basemaps',
        group: 'Reference',
        thumbnailLegend: true,
        isNotUpdateable: true,
        supportCopyFeature: true,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Agriculture',
        name: 'Agriculture',
        type: 'TileWMS',
        serviceName: 'risk-planning:Agriculture',
        thumbnail: '/thumbnails/ag_crop.png',
        summary: 'Agricultural land identified from the Valuation cadastre.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Ag_Livestock',
        name: 'Livestock',
        type: 'TileWMS',
        serviceName: 'risk-planning:Ag_Livestock',
        thumbnail: '/thumbnails/ag_improvedornativepasture.png',
        summary:
            'Livestock areas identified from the Valuation cadastre (rural land use classes).',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Ag_Horticulture',
        name: 'Horticulture',
        type: 'TileWMS',
        serviceName: 'risk-planning:Ag_Horticulture',
        thumbnail: '/thumbnails/ag_horticulturefruitnuttrees.png',
        summary:
            'Horticulture from the Valuation cadastre (fruit, nut, vegetable, intensive operations, vineyards).',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Ag_Poultry',
        name: 'Poultry',
        type: 'TileWMS',
        serviceName: 'risk-planning:Ag_Poultry',
        thumbnail: '/thumbnails/ag_intensiveanimalproduction.png',
        summary:
            'Poultry and intensive animal production facilities identified from the Valuation cadastre.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Bldg_Commercial',
        name: 'Commercial',
        type: 'TileWMS',
        serviceName: 'risk-planning:Bldg_Commercial',
        thumbnail: '/thumbnails/commercial.svg',
        summary: 'Commercial Building',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Bldg_Industrial',
        name: 'Industrial',
        type: 'TileWMS',
        serviceName: 'risk-planning:Bldg_Industrial',
        thumbnail: '/thumbnails/industry.svg',
        summary: 'Industrial Building',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Elect_PowerGeneration',
        name: 'Electricity generation',
        type: 'TileWMS',
        serviceName: 'risk-planning:Elect_PowerGeneration',
        thumbnail: '/thumbnails/power_station_coal.png',
        summary:
            "In-service generation sites from SARIG's All Power Generation layer.",
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Elect_SAPN_OverheadLV',
        name: 'SAPN Overhead Low Voltage Lines',
        type: 'TileWMS',
        serviceName: 'risk-planning:Elect_SAPN_OverheadLV',
        thumbnail: '/thumbnails/elect_trans_mains_lv.png',
        summary: 'SA Power Networks overhead low-voltage lines.',
        section: 'Assets',
        group: 'Other',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        legendRenderingOptions: {
            globalType: 'Line',
        },
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Elect_SAPN_OverheadHV',
        name: 'SAPN Overhead High Voltage Lines',
        type: 'TileWMS',
        serviceName: 'risk-planning:Elect_SAPN_OverheadHV',
        thumbnail: '/thumbnails/elect_trans_mains.png',
        summary: 'SA Power Networks overhead high-voltage lines.',
        section: 'Assets',
        group: 'Other',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        legendRenderingOptions: {
            globalType: 'Line',
        },
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Plantations_Forestry',
        name: 'Forestry',
        type: 'TileWMS',
        serviceName: 'risk-planning:Plantations_Forestry',
        thumbnail: '/thumbnails/plantations_hardwood.png',
        summary:
            'Forestry plantations (hardwood, softwood, and native) from the Valuation cadastre.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Infra_Telecom',
        name: 'Telecommunications',
        type: 'TileWMS',
        serviceName: 'risk-planning:Infra_Telecom',
        thumbnail: '/thumbnails/communications-tower.svg',
        summary:
            'Telecommunications assets (towers and exchanges) from the Valuation cadastre.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'MockRoleRestricted_TestLayer',
        name: 'Role Restriction Test Layer (Mock)',
        type: 'TileWMS',
        serviceName: 'risk-planning:MockRoleRestricted_TestLayer',
        thumbnail: '/thumbnails/communications-tower.svg',
        summary:
            'Mock layer for UAT testing of the role-based layer restriction system. Visible to BMC Executive Officer and above; hidden from BMC Member.',
        section: 'Assets',
        group: 'Other',
        isNotUpdateable: true,
    },
    {
        id: 'Enviro_NPWSA_Reserves',
        name: 'NPWSA Reserves',
        type: 'TileWMS',
        serviceName: 'risk-planning:Enviro_NPWSA_Reserves',
        thumbnail: '/thumbnails/national_park.png',
        summary:
            'National Parks and Wildlife SA reserves treated as environmental assets.',
        section: 'Boundaries and Basemaps',
        group: 'Reference',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Admin_BMC',
        name: 'Bushfire Management Areas',
        type: 'TileWMS',
        serviceName: 'risk-planning:Admin_BMC',
        thumbnail: '/thumbnails/bfmc.png',
        summary: 'SA Bushfire Management Areas (CFS RDS).',
        section: 'Boundaries and Basemaps',
        group: 'Reference',
        isNotUpdateable: true,
    },
    {
        id: 'Admin_LGA',
        name: 'Local Government Areas',
        type: 'TileWMS',
        serviceName: 'risk-planning:Admin_LGA',
        thumbnail: '/thumbnails/lga.png',
        summary: 'SA Local Government Area boundaries.',
        section: 'Boundaries and Basemaps',
        group: 'Reference',
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Fire_History',
        name: 'Fire History',
        type: 'TileWMS',
        serviceName: 'risk-planning:Fire_History',
        thumbnail: '/thumbnails/fire_history.png',
        summary: 'Historic fire footprints.',
        section: 'Risk Results',
        group: 'Fire Behaviour',
        isNotUpdateable: true,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Bldg_Residential',
        name: 'Residential (property centroids)',
        type: 'TileWMS',
        serviceName: 'risk-planning:Bldg_Residential',
        thumbnail: '/thumbnails/home.svg',
        summary:
            'Residential areas including urban bushland interface areas and rural properties.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Bldg_Schools',
        name: 'School',
        type: 'TileWMS',
        serviceName: 'risk-planning:Bldg_Schools',
        thumbnail: '/thumbnails/town-hall.svg',
        summary: 'Schools identified from the Valuation cadastre.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Bldg_Hospitals',
        name: 'Hospital',
        type: 'TileWMS',
        serviceName: 'risk-planning:Bldg_Hospitals',
        thumbnail: '/thumbnails/hospital-JP.svg',
        summary: 'Hospitals identified from the Valuation cadastre.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Bldg_CareFacilities',
        name: 'Care facility',
        type: 'TileWMS',
        serviceName: 'risk-planning:Bldg_CareFacilities',
        thumbnail: '/thumbnails/building.svg',
        summary:
            'Care facilities (nursing homes and residential aged care) from the Valuation cadastre.',
        section: 'Assets',
        group: 'Property Land Use Based',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: '"SA"',
            },
        ],
        isNotUpdateable: true,
        identifyRenderOptions: { dateOnlyFields: ['date_sourced'] },
    },
    {
        id: 'Adhoc_Pt',
        name: 'Adhoc Points',
        type: 'TileWMS',
        serviceName: 'risk-planning:Adhoc_Pt',
        thumbnail: '/thumbnails/adhoc_pt.png',
        thumbnailLegend: true,
        summary: 'Adhoc point datasets.',
        section: 'Assets',
        group: 'Adhoc',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
        isNotUpdateable: true,
    },
    {
        id: 'Adhoc_Line',
        name: 'Adhoc Lines',
        type: 'TileWMS',
        serviceName: 'risk-planning:Adhoc_Line',
        thumbnail: '/thumbnails/adhoc_line.png',
        summary: 'Adhoc line datasets.',
        section: 'Assets',
        group: 'Adhoc',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
        isNotUpdateable: true,
        legendRenderingOptions: {
            globalType: 'Line',
        },
    },
    {
        id: 'Adhoc_Poly',
        name: 'Adhoc Polygons',
        type: 'TileWMS',
        serviceName: 'risk-planning:Adhoc_Poly',
        thumbnail: '/thumbnails/adhoc_poly.png',
        summary: 'Adhoc polygon datasets.',
        section: 'Assets',
        group: 'Adhoc',
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
        isNotUpdateable: true,
    },
    // These layers are backed by PostgreSQL materialized views (MVW_*) in the geoserver schema.
    // The original NSW BIP design (bfrp-db-scripts/init/08-views.sql) states they are refreshed
    // by a scheduled ETL process — not by the API. No such scheduler exists in the SA platform yet,
    // so the views only contain data from the last manual refresh (seed-dev-data.sql) and will not
    // reflect live plan/pilot-area changes. Hidden until a refresh mechanism is in place.
    // {
    //     id: 'MVW_Bfrp_Plan_Active_BFRMP',
    //     name: 'BMAP Status',
    //     type: 'TileWMS',
    //     serviceName: 'risk-planning:MVW_Bfrp_Plan_Active',
    //     thumbnail: '/thumbnails/temperature.png',
    //     summary: 'BMAP Plan (Active)',
    //     section: 'Boundaries and Basemaps',
    //     group: 'Bushfire Management Area Plans',
    //     isNotUpdateable: true,
    //     filters: [
    //         {
    //             fieldName: 'PlanType',
    //             operator: '=',
    //             expression: '"bfrmp"',
    //         },
    //     ],
    // },
    // {
    //     id: 'MVW_FocusArea_Approved',
    //     name: 'Pilot Area (Approved)',
    //     type: 'TileWMS',
    //     serviceName: 'risk-planning:MVW_FocusArea_Approved',
    //     thumbnail: '/thumbnails/temperature.png',
    //     summary: 'Pilot Area (Approved)',
    //     section: 'Boundaries and Basemaps',
    //     group: 'Bushfire Management Area Plans',
    //     isNotUpdateable: true,
    // },
    // {
    //     id: 'MVW_FocusArea_Draft',
    //     name: 'Pilot Area (Draft)',
    //     type: 'TileWMS',
    //     serviceName: 'risk-planning:MVW_FocusArea_Draft',
    //     thumbnail: '/thumbnails/temperature.png',
    //     summary: 'Pilot Area (Draft)',
    //     section: 'Boundaries and Basemaps',
    //     group: 'Bushfire Management Area Plans',
    //     isNotUpdateable: true,
    // },
    // {
    //     id: 'MVW_FMR_Approved',
    //     name: 'Fuel Management Register (Approved)',
    //     type: 'TileWMS',
    //     serviceName: 'risk-planning:MVW_FMR_Approved',
    //     thumbnail: '/thumbnails/temperature.png',
    //     summary: 'Fuel Management Register (Approved)',
    //     section: 'Boundaries and Basemaps',
    //     group: 'Bushfire Management Area Plans',
    //     isNotUpdateable: true,
    // },
    // {
    //     id: 'MVW_FMR_Draft',
    //     name: 'Fuel Management Register (Draft)',
    //     type: 'TileWMS',
    //     serviceName: 'risk-planning:MVW_FMR_Draft',
    //     thumbnail: '/thumbnails/temperature.png',
    //     summary: 'Fuel Management Register (Draft)',
    //     section: 'Boundaries and Basemaps',
    //     group: 'Bushfire Management Area Plans',
    //     isNotUpdateable: true,
    // },
    ...riskMatrixLayers,
    {
        id: 'FireBehaviour_Ignitions',
        name: 'Ignitions',
        type: 'TileWMS',
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/ignitions.png',
        summary: 'Modelled ignition density — placeholder for SA UAT.',
        section: 'Risk Results',
        group: 'Fire Behaviour',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    },
    {
        id: 'FireBehaviour_FuelLoad',
        name: 'Fuel Load',
        type: 'TileWMS',
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/fuel_load.png',
        summary: 'Fuel load — placeholder for SA UAT.',
        section: 'Risk Results',
        group: 'Fire Behaviour',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    },
    {
        id: 'FireBehaviour_FuelType',
        name: 'Fuel Type (NBIC)',
        type: 'TileWMS',
        serviceName: 'risk-planning:FireBehaviour_FuelType',
        thumbnail: '/thumbnails/fuel_type.png',
        summary:
            'CSIRO NBIC fuel type classification (Release 2). 90 m raster clipped to SA.',
        section: 'Risk Results',
        group: 'Fire Behaviour',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
    },
    {
        id: 'Preliminary_Ignitions',
        name: 'Preliminary Ignitions',
        type: 'TileWMS',
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/ignitions_pt.png',
        summary:
            'Preliminary ignition density (Workshop 1) — placeholder for SA UAT.',
        section: 'Risk Results',
        group: 'Preliminary Risk Layers',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    },
    {
        id: 'Preliminary_FuelLoad',
        name: 'Preliminary Fuel Load',
        type: 'TileWMS',
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/fuel_load.png',
        summary: 'Preliminary fuel load (Workshop 1) — placeholder for SA UAT.',
        section: 'Risk Results',
        group: 'Preliminary Risk Layers',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    },
    {
        id: 'Preliminary_TimesBurnt',
        name: 'Preliminary Times Burnt',
        type: 'TileWMS',
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/temperature.png',
        summary:
            'Preliminary times-burnt (Workshop 1) — placeholder for SA UAT.',
        section: 'Risk Results',
        group: 'Preliminary Risk Layers',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    },
    {
        id: 'Preliminary_ResidentialRiskByDensity',
        name: 'Preliminary Residential Risk (by Density)',
        type: 'TileWMS',
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/residential_density.png',
        summary:
            'Preliminary residential risk by density (Workshop 1) — placeholder for SA UAT.',
        section: 'Risk Results',
        group: 'Preliminary Risk Layers',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    },
    {
        id: 'Preliminary_ResidentialEmbersRisk',
        name: 'Preliminary Residential Embers Risk',
        type: 'TileWMS',
        serviceName: 'risk-planning:RiskMock',
        thumbnail: '/thumbnails/temperature.png',
        summary:
            'Preliminary residential embers risk (Workshop 1) — placeholder for SA UAT.',
        section: 'Risk Results',
        group: 'Preliminary Risk Layers',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
        filters: [
            {
                fieldName: 'data_src',
                operator: '=',
                expression: 'group?group.name:guide.selectedBFMC?.code',
            },
        ],
    },
    // -------------------------------------------------------------------------
    // EXAMPLE RISK LAYERS FROM DEW (R1 interim — remove when data is retired)
    // No data_src filter: these layers are statewide and appear for all BMCs
    // at all stages. All IDs/tables/GeoServer configs are prefixed ClientRisk_
    // for easy grep-and-delete cleanup.
    // -------------------------------------------------------------------------
    {
        id: 'ClientRisk_AMLR_FL_HouseLossIgnition',
        name: 'AMLR/Fleurieu — House Loss Per Ignition',
        type: 'TileWMS',
        serviceName: 'risk-planning:ClientRisk_AMLR_FL_HouseLossIgnition',
        thumbnail: '/thumbnails/ignitions_pt.png',
        classifiedLegend: true,
        legendExcludeLabels: ['0'],
        summary:
            'Client-provided R1 data: AMLR/Fleurieu house loss per ignition point layer (2024). Not yet aligned to the standard risk model.',
        section: 'Risk Results',
        group: 'EXAMPLE RISK LAYERS FROM DEW',
        isNotUpdateable: true,
        opacity: 0.7,
    },
    {
        id: 'ClientRisk_AMLR_FL_PostBurnHouseLoss',
        name: 'AMLR/Fleurieu — Average House Loss',
        type: 'TileWMS',
        serviceName: 'risk-planning:ClientRisk_AMLR_FL_PostBurnHouseLoss',
        wmsStyles: 'client_risk_amlr_postburn_houseloss',
        thumbnail: '/thumbnails/temperature.png',
        summary:
            'Client-provided R1 data: AMLR/Fleurieu post-burn average house loss raster (2024). Not yet aligned to the standard risk model.',
        section: 'Risk Results',
        group: 'EXAMPLE RISK LAYERS FROM DEW',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
    },
    {
        id: 'ClientRisk_KI_RiskSimulation',
        name: 'Kangaroo Island — Risk Simulation (Average Intensity)',
        type: 'TileWMS',
        serviceName: 'risk-planning:ClientRisk_KI_RiskSimulation',
        thumbnail: '/thumbnails/temperature.png',
        wmsStyles: 'client_risk_ki_risk_simulation',
        classifiedLegend: true,
        legendExcludeLabels: ['0'],
        summary:
            'Client-provided R1 data: Kangaroo Island risk simulation average fire intensity (99.5th percentile). Not yet aligned to the standard risk model.',
        section: 'Risk Results',
        group: 'EXAMPLE RISK LAYERS FROM DEW',
        isNotUpdateable: true,
        opacity: 0.7,
    },
    {
        id: 'ClientRisk_KI_IgnitionImpact',
        name: 'Kangaroo Island — Ignitions Resulting In Asset Loss',
        type: 'TileWMS',
        serviceName: 'risk-planning:ClientRisk_KI_IgnitionImpact',
        thumbnail: '/thumbnails/ignitions_pt.png',
        summary:
            'Client-provided R1 data: Kangaroo Island ignition asset impact points (+10 years, fuel 99.5%). Not yet aligned to the standard risk model.',
        section: 'Risk Results',
        group: 'EXAMPLE RISK LAYERS FROM DEW',
        isNotUpdateable: true,
        opacity: 0.7,
    },
    {
        id: 'ClientRisk_MM_TimesBurnt',
        name: 'Murray Mallee — Times Burnt',
        type: 'TileWMS',
        serviceName: 'risk-planning:ClientRisk_MM_TimesBurnt',
        wmsStyles: 'client_risk_mm_times_burnt',
        thumbnail: '/thumbnails/temperature.png',
        summary:
            'Client-provided R1 data: Murray Mallee times burnt raster (2025). Not yet aligned to the standard risk model.',
        section: 'Risk Results',
        group: 'EXAMPLE RISK LAYERS FROM DEW',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
    },
    {
        id: 'ClientRisk_MM_HouseLoss',
        name: 'Murray Mallee — Average House Loss',
        type: 'TileWMS',
        serviceName: 'risk-planning:ClientRisk_MM_HouseLoss',
        wmsStyles: 'client_risk_mm_houseloss',
        thumbnail: '/thumbnails/temperature.png',
        summary:
            'Client-provided R1 data: Murray Mallee mean average house loss raster (2025). Not yet aligned to the standard risk model.',
        section: 'Risk Results',
        group: 'EXAMPLE RISK LAYERS FROM DEW',
        isNotUpdateable: true,
        noPopup: true,
        opacity: 0.7,
    },
    {
        id: 'BMC_Mask',
        name: 'BMC Mask',
        type: 'TileWMS',
        serviceName: 'risk-planning:Admin_BMC_Mask',
        wmsStyles: 'bmc_mask_precomputed',
        isHidden: true,
        isBaseMap: false,
        noLegend: true,
        isNotUpdateable: true,
        section: 'Boundaries and Basemaps',
        group: 'Boundaries',
        filters: [
            {
                fieldName: 'BMC',
                operator: '=',
                expression: 'guide.selectedBFMC?.name',
            },
        ],
    },
];

/**
 * The Layer Configurations
 */
export const layerConfig: LayerConfig[] =
    unprocessedlayerConfig.map<LayerConfig>((config) => ({
        ...config,
        status: 'normal',
        lastUpdated: null,
        timeSteps: null,
    }));
/**
 * A list of all the Layer Objects
 */
export const layers = layerConfig
    .map((config) => {
        if (config.type === 'OSM')
            return new OSMLayer({ config, opacity: config.opacity });

        if (config.type === 'TileWMS')
            return new TileWMSLayer({ config, opacity: config.opacity });

        if (config.type === 'TileMapServer')
            return new MapServerTileLayer({
                config,
                url: config.url,
                opacity: config.opacity,
            });

        if (config.type === 'VectorTile')
            return new VectorTileLayer({ config, opacity: config.opacity });

        if (config.type === 'XYZ' && config.url != null)
            return new XYZLayer({
                config,
                url: config.url,
                opacity: config.opacity,
            });

        if (config.type === 'Composite')
            return new CompositeLayer({ config, opacity: config.opacity });

        if (config.type === 'ImageArcGISRestLayer' && config.url != null) {
            return new ImageArcGISRestLayer({
                config,
                url: config.url,
                opacity: config.opacity,
            });
        }
        if (config.type === 'CommentLayer')
            return new CommentLayer({
                config,
                opacity: config.opacity,
            });

        if (config.type === 'RedlineLayer') return new RedlineLayer(config);

        if (config.type === 'WMSEditableLayer')
            return new WMSEditableLayer(config);

        return null;
    })
    .filter((l) => l != null) as AbstractLayer[];

/**
 * @param id The Id of the layer to find
 * @param config The list of Layer Configurations to search
 * (if not provided it will default to the full list)
 * @returns The found LayerConfig or null if not found
 */
export const findConfig = <T extends { id: Layer.Id } = LayerConfig>(
    id: LiteralUnion<Layer.Id>,
    config?: T[] | null,
): T | undefined =>
    ((config != null ? config : layerConfig) as T[]).find((l) => l.id === id);

/**
 * @param id The Layer Id to find
 * @returns The Layer Object that contains uses the provided Id
 */
export const findLayer = (id?: LiteralUnion<Layer.Id> | null) =>
    id && layers.find((l) => l.config.id === id);

/**
 *
 * @param selectedLayerIds a collection of id's the user has selected
 * @returns returns only truthy values of type AbstractLayer.
 */
export const queryableLayersMap = (selectedLayerIds: string[] | null) => {
    if (!selectedLayerIds) {
        return [];
    }

    return selectedLayerIds
        .map((id) => findLayer(id))
        .filter((falsyValues) => !!falsyValues) as Exclude<
        AbstractLayer[],
        null | undefined
    >;
};

/**
 * Will make all layers invisible except for any in the exception list.
 * @param animate A Flag, if true will animate all layers off. If false,
 * they're turned off immidiately.
 * @param except A list of layers to not include when making invisible.
 */
export const hideAllLayers = (animate?: boolean, except: Layer.Id[] = []) => {
    if (animate) {
        layers.forEach((layer) => {
            if (layer.visible && !except.includes(layer.id)) {
                const oldOpacity = layer.opacity;
                layer.animateOpacity(0, () => {
                    layer.visible = false;
                    layer.opacity = oldOpacity;
                });
            }
        });
        return;
    }
    layers.forEach((layer) => {
        if (!except.includes(layer.id)) layer.visible = false;
    });
};

export const getLayers = makeMockAPICall<LayerConfig[], void, null>(
    () => {},
    async () => layerConfig,
    null,
    0,
);

export const getLatestLayerStartDate = (
    lyrs: LayerConfig[] | undefined,
): Date | null | undefined => {
    let latestLayerStart = null;
    if (lyrs) {
        for (let i = 0; i < lyrs.length; i += 1) {
            const layer = lyrs[i];
            // initially assume layerStart is 1 full year in the future
            if (layer.isTimeEnabled && layer.timeSteps) {
                let layerStart = new Date(
                    new Date().setFullYear(new Date().getFullYear() + 1),
                );
                for (let j = 0; j < layer.timeSteps?.length; j += 1) {
                    if (layer.timeSteps[j] <= layerStart) {
                        layerStart = new Date(layer.timeSteps[j].getTime());
                    }
                }
                if (!latestLayerStart || layerStart > latestLayerStart) {
                    latestLayerStart = layerStart;
                }
            }
        }
    }
    return latestLayerStart;
};

export const bookmarkFilter = (configs: LayerConfig[]): LayerConfig[] => {
    const bookmarkIdsUnserialised = localStorage.getItem(
        LOCALSTORAGE.BOOKMARKS,
    );
    if (bookmarkIdsUnserialised == null) return [];
    try {
        const bookmarkIds = JSON.parse(bookmarkIdsUnserialised) as string[];
        return configs.filter((l) => bookmarkIds.indexOf(l.id) > -1);
    } catch (_e) {
        return [];
    }
};

export const getBookmarkedLayers = makeMockAPICall<
    LayerConfig[],
    void,
    LayerConfig[]
>(
    () => {},
    async (_, payload) => bookmarkFilter(await getLayers(payload)),
    [],
    0,
);

export const addBookmark = makeMockAPICall<null, { config: LayerConfig }>(
    ({ config }) => {
        const bookmarkIdsUnserialised = localStorage.getItem(
            LOCALSTORAGE.BOOKMARKS,
        );

        const bookmarkIds =
            bookmarkIdsUnserialised == null
                ? []
                : (JSON.parse(bookmarkIdsUnserialised) as string[]);
        bookmarkIds.push(config.id);
        localStorage.setItem(
            LOCALSTORAGE.BOOKMARKS,
            JSON.stringify(bookmarkIds),
        );
    },
    () => null,
    null,
    0,
);

export const removeBookmark = makeMockAPICall<null, { config: LayerConfig }>(
    ({ config }) => {
        const bookmarkIdsUnserialised = localStorage.getItem(
            LOCALSTORAGE.BOOKMARKS,
        );

        const bookmarkIds =
            bookmarkIdsUnserialised == null
                ? []
                : (JSON.parse(bookmarkIdsUnserialised) as string[]);
        localStorage.setItem(
            LOCALSTORAGE.BOOKMARKS,
            JSON.stringify(bookmarkIds.filter((bm) => bm !== config.id)),
        );
    },
    () => null,
    null,
    0,
);

export const clearBookmarks = makeMockAPICall<null>(
    () => {
        localStorage.setItem(LOCALSTORAGE.BOOKMARKS, JSON.stringify([]));
    },
    () => null,
    null,
    0,
);
